export type OntologyObservationDimension = "typography" | "radius" | "density" | "color" | "responsive";
export type OntologyObservationComparison = "distinct-across-releases" | "preserved-across-releases";

export interface OntologyDeclaredRoleMeasurementV1 {
  role: string;
  measurement: {
    measurementId: string;
    dimension: OntologyObservationDimension;
    selectorKind: "design-handle";
    selectorValue: string;
    observableProperties: string[];
    comparison: OntologyObservationComparison;
    expectedDistinctAcrossReleases: boolean;
    invariantRefs: string[];
    sourceRefs: string[];
  };
}

export interface OntologyObservationProbeV1 {
  measurementId: string;
  dimension: OntologyObservationDimension;
  role: string;
  selectorKind: "design-handle";
  selectorValue: string;
  observableProperties: string[];
  comparison: OntologyObservationComparison;
  expectedDistinctAcrossReleases: boolean;
  invariantRefs: string[];
  sourceRefs: string[];
}

export interface CompileOntologyObservationPlanResultV1 {
  probes: OntologyObservationProbeV1[];
  blockers: string[];
}

export interface OntologyObservationSnapshotV1 {
  releaseId: string;
  measurementId: string;
  role: string;
  elementCount: number;
  values: Array<Record<string, string>>;
}

const SAFE_PROPERTIES = new Set([
  "background-color", "border-bottom-left-radius", "border-bottom-right-radius", "border-top-color",
  "border-top-left-radius", "border-top-right-radius", "color", "display", "flex-direction",
  "font-family", "font-size", "font-weight", "gap", "grid-template-columns", "letter-spacing",
  "line-height", "margin-bottom", "margin-left", "margin-right", "margin-top", "padding-bottom",
  "padding-left", "padding-right", "padding-top", "width",
]);
const REQUIRED_INVARIANTS = ["structure", "ids", "events", "state", "input", "selection", "focus", "scroll"];
const HANDLE = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,191}$/u;

export function compileOntologyObservationPlanV1(input: {
  measurements: OntologyDeclaredRoleMeasurementV1[];
  roleRefs: string[];
  declaredDimensions?: Record<string, OntologyObservationDimension>;
}): CompileOntologyObservationPlanResultV1 {
  if (input.roleRefs.length > 512 || input.measurements.length > 4_096) {
    return { probes: [], blockers: ["ontology-observation-resource-limit"] };
  }
  const byRole = new Map(input.measurements.map((entry) => [entry.role, entry.measurement]));
  const probes: OntologyObservationProbeV1[] = [];
  const blockers: string[] = [];
  for (const role of unique(input.roleRefs)) {
    const measurement = byRole.get(role);
    if (!measurement) {
      blockers.push(`ontology-observation-measurement-missing:${role}`);
      continue;
    }
    if (measurement.selectorKind !== "design-handle" || measurement.selectorValue !== role || !HANDLE.test(role)) {
      blockers.push(`ontology-observation-selector-invalid:${role}`);
      continue;
    }
    if (measurement.observableProperties.length === 0 || measurement.observableProperties.length > 16) {
      blockers.push(`ontology-observation-property-count-invalid:${role}`);
      continue;
    }
    const unsafe = measurement.observableProperties.filter((property) => !SAFE_PROPERTIES.has(property));
    if (unsafe.length > 0) {
      blockers.push(...unsafe.map((property) => `ontology-observation-property-unsafe:${role}:${property}`));
      continue;
    }
    const missingInvariants = REQUIRED_INVARIANTS.filter((invariant) => !measurement.invariantRefs.includes(invariant));
    if (missingInvariants.length > 0) {
      blockers.push(...missingInvariants.map((invariant) => `ontology-observation-invariant-missing:${role}:${invariant}`));
      continue;
    }
    const declaredDimension = input.declaredDimensions?.[role];
    if (declaredDimension && declaredDimension !== measurement.dimension) {
      blockers.push(`ontology-observation-dimension-mismatch:${role}`);
      continue;
    }
    probes.push({
      measurementId: measurement.measurementId,
      dimension: measurement.dimension,
      role,
      selectorKind: measurement.selectorKind,
      selectorValue: measurement.selectorValue,
      observableProperties: uniquePreservingOrder(measurement.observableProperties),
      comparison: measurement.comparison,
      expectedDistinctAcrossReleases: measurement.expectedDistinctAcrossReleases,
      invariantRefs: unique(measurement.invariantRefs),
      sourceRefs: unique(measurement.sourceRefs),
    });
  }
  return {
    probes: probes.sort((left, right) => left.role.localeCompare(right.role)),
    blockers: unique(blockers),
  };
}

export function observeOntologyValidationPlanV1(input: {
  releaseId: string;
  probes: OntologyObservationProbeV1[];
  resolveDesignHandle: (handle: string) => Element[];
  readComputedStyle?: (element: Element) => CSSStyleDeclaration;
}): { snapshots: OntologyObservationSnapshotV1[]; blockers: string[] } {
  const readStyle = input.readComputedStyle ?? ((element) => getComputedStyle(element));
  const blockers: string[] = [];
  const snapshots = input.probes.map((probe): OntologyObservationSnapshotV1 => {
    const elements = input.resolveDesignHandle(probe.selectorValue);
    if (elements.length === 0) blockers.push(`ontology-observation-target-missing:${probe.role}`);
    if (elements.length > 32) blockers.push(`ontology-observation-target-limit:${probe.role}`);
    const values = elements.slice(0, 32).map((element) => {
      const style = readStyle(element);
      return Object.fromEntries(probe.observableProperties.map((property) => [
        property,
        normalizeObservedValue(style.getPropertyValue(property)),
      ]));
    });
    return {
      releaseId: input.releaseId,
      measurementId: probe.measurementId,
      role: probe.role,
      elementCount: elements.length,
      values,
    };
  });
  return { snapshots, blockers: unique(blockers) };
}

export function compareOntologyObservationSnapshotsV1(input: {
  probes: OntologyObservationProbeV1[];
  snapshots: OntologyObservationSnapshotV1[];
}): string[] {
  const blockers: string[] = [];
  for (const probe of input.probes) {
    const snapshots = input.snapshots.filter((snapshot) => snapshot.measurementId === probe.measurementId);
    if (snapshots.length < 2) {
      blockers.push(`ontology-observation-release-coverage-missing:${probe.role}`);
      continue;
    }
    const values = snapshots.map((snapshot) => JSON.stringify(snapshot.values));
    const distinct = new Set(values).size > 1;
    if (probe.comparison === "distinct-across-releases" && !distinct) {
      blockers.push(`ontology-observation-release-not-distinct:${probe.role}`);
    }
    if (probe.comparison === "preserved-across-releases" && distinct) {
      blockers.push(`ontology-observation-release-not-preserved:${probe.role}`);
    }
  }
  return unique(blockers);
}

function normalizeObservedValue(value: string): string {
  return value.trim().replace(/\s+/gu, " ").slice(0, 256);
}

function unique(values: string[]): string[] {
  return [...new Set(values)].sort((left, right) => left.localeCompare(right));
}

function uniquePreservingOrder(values: string[]): string[] {
  return [...new Set(values)];
}
